/** @jsx jsx */

import { Fragment, useEffect, useState, ReactNode } from 'react';
import { at, camelCase, every, get, isEqual, negate, some } from 'lodash';

import { Margin, Surface, Tabs, Text } from '@zapier/design-system';
import { DropdownMenu } from '@zapier/common-components';
import { jsx } from '@emotion/core';
import { usePrevious } from '@zapier/common-hooks';

import Divider from 'app/developer-v3/components/Divider';
import FieldMessage, {
  FieldType,
} from 'app/developer-v3/components/FieldMessage';
import AsyncButton from 'app/developer-v3/components//AsyncButton';
import {
  getBorderColor,
  getBorderColorName,
  prettyFormatJsonString,
  quickCopy,
} from 'app/developer-v3/utils';

import Bundle from './Bundle';
import Console from './Console';
import Http from './Http';
import RefreshLogs from './RefreshLogs';
import Response from './Response';
import Setup from './Setup';
import StatusIndicator from './StatusIndicator';
import Styles from './RequestTester.styles';
import { TABS_DICTIONARY, SETUP_TAB } from './constants';
import { getRequestDescription } from './utils';

import type { InputFieldSchema } from 'app/developer-v3/platformSchema/field';
import type {
  ObjectType,
  OperationMethodType,
  TestLogsType,
  TestRequestType,
  ViolationTypes,
} from 'app/developer-v3/types';
import type { RequestSchema } from 'app/developer-v3/platformSchema/request';

import type { RequestType, TestData } from './types';

type TabItem = {
  text: ReactNode;
  href: string;
  disabled?: boolean;
};

type Info = {
  type?: ViolationTypes;
  message?: string;
};

type Props = {
  appName: string;
  canConnectAuth: boolean;
  clearRequest: () => Promise<any>;
  executeRequest: (
    request: RequestSchema,
    authenticationId: string,
    operationMethod: OperationMethodType,
    testData: TestData
  ) => Promise<any>;
  getLogs: () => Promise<any>;
  initialTab: TabItem;
  inputFields: InputFieldSchema[];
  needsAuthMessage: ReactNode;
  requests: Array<RequestType>;
  selectedApi: string;
  testLogs: TestLogsType;
  testRequest: TestRequestType;
  testType: ObjectType;
  meta?: {
    info?: Info;
  };
};

function RequestTester({
  appName,
  canConnectAuth,
  clearRequest,
  executeRequest,
  getLogs,
  initialTab,
  inputFields,
  needsAuthMessage,
  requests,
  selectedApi,
  testLogs,
  testRequest,
  testType,
  meta,
}: Props) {
  const [activeTab, setActiveTab] = useState<TabItem>(initialTab);
  const [badgeCountBundle, setBadgeCountBundle] = useState<number>(0);
  const [badgeCountHttp, setBadgeCountHttp] = useState<number>(0);
  const [badgeCountConsole, setBadgeCountConsole] = useState<number>(0);
  const [request, setRequest] = useState<RequestType>(get(requests, '0'));
  const [testData, setTestData] = useState<string>(
    prettyFormatJsonString({
      inputData: inputFields.reduce((inputData, { key }) => {
        inputData[key] = '';
        return inputData;
      }, {}),
      authData: {},
      environment: {},
      subscribeData: {},
      cleanedRequest: {
        url: 'https://example.com/hook-request-origin',
        method: 'POST',
        body: {},
        headers: { 'Content-Type': 'application/json' },
      },
    })
  );
  const [authId, setAuthId] = useState<string>('');
  const prevTestLogs = usePrevious(testLogs);

  useEffect(() => {
    if (request) {
      quickCopy(requests).forEach(req => {
        if (
          isEqual(request.operationMethod, req.operationMethod) &&
          !isEqual(request.request, req.request)
        ) {
          setRequest(req);
        }
      });
    }

    const paths = ['bundle.isLoading', 'http.isLoading', 'console.isLoading'];
    const areTestLogsDoneLoading =
      some(at(prevTestLogs, paths)) &&
      every(at(testLogs, paths), negate(Boolean));

    if (areTestLogsDoneLoading) {
      setBadgeCountBundle(get(testLogs, ['bundle', 'logs'], []).length);
      setBadgeCountHttp(get(testLogs, ['http', 'logs'], []).length);
      setBadgeCountConsole(get(testLogs, ['console', 'logs'], []).length);
    }
  }, [prevTestLogs, requests, request, testLogs]);

  const changeTab = (tab: TabItem) => {
    if (tab.href === TABS_DICTIONARY.BUNDLE) {
      setBadgeCountBundle(0);
    } else if (tab.href === TABS_DICTIONARY.HTTP) {
      setBadgeCountHttp(0);
    } else {
      setBadgeCountConsole(0);
    }

    setActiveTab(tab);
  };

  const onClickTryRequest = async () => {
    try {
      setBadgeCountBundle(0);
      setBadgeCountHttp(0);
      setBadgeCountConsole(0);

      await executeRequest(
        request.request,
        authId,
        request.operationMethod,
        JSON.parse(testData)
      );
    } catch (e) {
      // Noop.
    } finally {
      changeTab({ text: 'Response', href: '#response' });
    }
  };

  const onChooseRequest = ({ value: index }) => {
    setRequest(requests[index]);
    changeTab(SETUP_TAB);
  };

  const navbarItems = [
    SETUP_TAB,
    { text: 'Response', href: '#response' },
    {
      text: 'Bundle',
      badgeText: badgeCountBundle ? badgeCountBundle.toString() : null,
      href: TABS_DICTIONARY.BUNDLE,
      disabled: testLogs.bundle.isLoading,
    },
    {
      text: 'HTTP',
      badgeText: badgeCountHttp ? badgeCountHttp.toString() : null,
      href: TABS_DICTIONARY.HTTP,
      disabled: testLogs.http.isLoading,
    },
    {
      text: 'Console',
      badgeText: badgeCountConsole ? badgeCountConsole.toString() : null,
      href: TABS_DICTIONARY.CONSOLE,
      disabled: testLogs.console.isLoading,
    },
  ];

  const info: Info = meta?.info ?? {};
  const inputBorderColor = getBorderColor(info.type);

  return (
    <div>
      {requests.length > 1 && (
        <div css={Styles.request(inputBorderColor)}>
          <DropdownMenu
            items={requests.map((req, index) => ({
              label: `${req.label}: ${getRequestDescription(req.request)}`,
              value: index,
            }))}
            label={
              <Fragment>
                <Text fontWeight={600} margin="0px 10px 0px 0px">
                  {get(request, ['label'], 'N/A')}
                </Text>
                {getRequestDescription(get(request, ['request']))}
              </Fragment>
            }
            menuIsBlock={true}
            onSelect={onChooseRequest}
          />
        </div>
      )}
      <Surface borderColor={getBorderColorName(info.type)}>
        <Tabs
          items={navbarItems}
          onChooseItem={item => setActiveTab(item)}
          selectedItemHref={activeTab.href}
        />
        {testRequest.status && (
          <Margin margin="20px 10px 10px 10px">
            <StatusIndicator requestStatus={testRequest.status} />
          </Margin>
        )}
        {activeTab.href === TABS_DICTIONARY.SETUP && (
          <Setup
            appName={appName}
            canConnectAuth={canConnectAuth}
            inputFields={inputFields}
            needsAuthMessage={needsAuthMessage}
            onChangeTestData={(data = '') => setTestData(data)}
            onClickTryRequest={onClickTryRequest}
            onReconnectedAuth={clearRequest}
            onSelectAuth={setAuthId}
            selectedApi={selectedApi}
            selectedAuthId={authId}
            testData={testData}
          />
        )}
        {activeTab.href === TABS_DICTIONARY.RESPONSE && (
          <Response
            errorMessage={
              testRequest.status === ''
                ? 'No test request made yet. Click "Test Setup" and "Try it!" at the bottom to make a test request and see its response here.'
                : testRequest.errorMessage
            }
            onClickTestAgain={() => changeTab(SETUP_TAB)}
            output={testRequest.output}
          />
        )}
        {activeTab.href === TABS_DICTIONARY.BUNDLE && (
          <Bundle
            isLoading={testLogs.bundle.isLoading}
            logs={testLogs.bundle.logs}
            onClickTestAgain={() => changeTab(SETUP_TAB)}
          />
        )}
        {activeTab.href === TABS_DICTIONARY.HTTP && (
          <Http
            isLoading={testLogs.http.isLoading}
            logs={testLogs.http.logs}
            onClickTestAgain={() => changeTab(SETUP_TAB)}
          />
        )}
        {activeTab.href === TABS_DICTIONARY.CONSOLE && (
          <Console
            isLoading={testLogs.console.isLoading}
            logs={testLogs.console.logs}
            onClickTestAgain={() => changeTab(SETUP_TAB)}
          />
        )}
        {activeTab.href !== TABS_DICTIONARY.RESPONSE && (
          <footer css={Styles.footer}>
            {activeTab.href === TABS_DICTIONARY.SETUP && (
              <Fragment>
                <div css={Styles.wrapper}>
                  <AsyncButton
                    variant="primary"
                    isDisabled={!request || testRequest.isExecuting}
                    isWorking={testRequest.isExecuting}
                    onPress={onClickTryRequest}
                  >
                    {`Test ${
                      testType === 'authentication'
                        ? 'Authentication'
                        : 'Your Request'
                    }`}
                  </AsyncButton>
                  {activeTab.href === TABS_DICTIONARY.SETUP &&
                    canConnectAuth &&
                    !inputFields.length && (
                      <Text tag="span">
                        Run your Test request using the account you connected to
                        make sure that everything is working.
                      </Text>
                    )}
                </div>
                <Divider />
              </Fragment>
            )}
            <RefreshLogs onClick={getLogs} />
          </footer>
        )}
      </Surface>
      {info && (
        <FieldMessage
          fieldKey="sample-output-editor"
          message={info.message}
          type={camelCase(`info ${info.type}`) as FieldType}
        />
      )}
    </div>
  );
}

RequestTester.defaultProps = {
  initialTab: SETUP_TAB,
  inputFields: [],
  requests: [],
};

export default RequestTester;
